import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { COMPANY, REDRESS } from "../../constants/legal";
import { SubmitWithdrawalAction } from "../../store/action";
import { usePageMeta } from "../../hooks/usePageMeta";

/**
 * /withdrawal — the online facility art. 11^1 of OUG 34/2014 requires.
 *
 * Public, and deliberately outside the dashboard. The people who need it most
 * are the ones who can no longer sign in: a lapsed subscriber, somebody whose
 * account was closed, somebody who never finished signing up. A form behind a
 * login would be exactly the obstacle the article exists to remove.
 *
 * Two required fields and nothing else. The law says the consumer need not
 * justify the decision, so the page says so too and leaves the reason box
 * optional — a form that demands a reason quietly contradicts the right it is
 * meant to deliver.
 *
 * Public section, so Tailwind tokens only (see FRONTEND-PATTERNS.md).
 */

const labelClass = "mb-1.5 block text-fluid-sm font-semibold text-ink-700";

const fieldClass =
  "w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-fluid-sm text-ink-900 placeholder:text-ink-400 transition focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20";

const errorFieldClass =
  "w-full rounded-lg border border-red-400 bg-white px-3 py-2.5 text-fluid-sm text-ink-900 placeholder:text-ink-400 transition focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20";

const Withdrawal = () => {
  const { i18n } = useTranslation();
  const ro = !i18n?.language?.startsWith("en");

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    orderReference: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  const t = (roText, enText) => (ro ? roText : enText);

  usePageMeta({
    title: `${t("Retragerea din contract", "Withdrawing from the contract")} | EasyPlanner`,
  });

  const change = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => {
      if (!previous[name]) return previous;
      const { [name]: _fixed, ...rest } = previous;
      return rest;
    });
  };

  const validate = () => {
    const found = {};

    if (!form.fullName.trim()) {
      found.fullName = t("Numele este obligatoriu.", "Your name is required.");
    }

    // Only a shape check. Whether the address exists is answered by the
    // confirmation arriving, and a stricter pattern here would reject valid
    // addresses on a form somebody is using to exercise a right.
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      found.email = t(
        "Introduceți o adresă de e-mail validă — acolo trimitem confirmarea.",
        "Enter a valid e-mail address — that is where the confirmation goes.",
      );
    }

    return found;
  };

  const submit = async (event) => {
    event.preventDefault();

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      document.getElementById(Object.keys(found)[0])?.focus();
      return;
    }

    setSending(true);
    setSubmitError(null);

    try {
      setResult(
        await SubmitWithdrawalAction({
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          orderReference: form.orderReference.trim() || null,
          message: form.message.trim() || null,
        }),
      );
    } catch (error) {
      setSubmitError(
        error?.response?.status === 429
          ? t(
              "Prea multe încercări. Așteptați un minut și trimiteți din nou.",
              "Too many attempts. Wait a minute and send it again.",
            )
          : t(
              `Cererea nu a putut fi trimisă. Încercați din nou, sau scrieți-ne direct la ${COMPANY.email} — o notificare prin e-mail este la fel de valabilă.`,
              `The request could not be sent. Try again, or write to us directly at ${COMPANY.email} — an e-mail notice is equally valid.`,
            ),
      );
    } finally {
      setSending(false);
    }
  };

  if (result) {
    const received = new Date(result.receivedAt).toLocaleString(
      ro ? "ro-RO" : "en-GB",
    );

    return (
      <main className="mx-auto w-full max-w-2xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="rounded-card border border-brand-200 bg-brand-50 p-6">
          <h1 className="m-0 text-fluid-lg font-bold text-ink-900">
            {t("Cererea a fost înregistrată", "Your request is recorded")}
          </h1>
          <p className="mt-3 text-fluid-sm leading-relaxed text-ink-700">
            {t(
              "Am înregistrat retragerea dumneavoastră din contract. Nu mai trebuie să faceți nimic.",
              "We have recorded your withdrawal from the contract. There is nothing further for you to do.",
            )}
          </p>
          <p className="mt-3 text-fluid-sm text-ink-700">
            {t("Data înregistrării: ", "Recorded on: ")}
            <strong className="text-ink-900">{received}</strong>
          </p>

          {/* The date is stated on screen as well as e-mailed, because it is
              what the fourteen days run from and the e-mail may not arrive. */}
          <p className="mt-3 text-fluid-sm text-ink-600">
            {result.confirmationSent
              ? t(
                  "V-am trimis confirmarea scrisă pe e-mail. Păstrați-o.",
                  "We have e-mailed you the written confirmation. Keep it.",
                )
              : t(
                  "Confirmarea pe e-mail nu a putut fi trimisă acum, dar cererea este înregistrată cu data de mai sus. Notați-o.",
                  "The confirmation e-mail could not be sent just now, but the request is recorded with the date above. Note it down.",
                )}
          </p>
          <p className="mt-3 text-fluid-sm text-ink-600">
            {t(
              "Dacă ați efectuat o plată, vă restituim suma pe același card în cel mult 14 zile.",
              "If you made a payment, we refund it to the same card within 14 days.",
            )}
          </p>
        </div>

        <Link
          to="/"
          className="mt-6 inline-block text-fluid-sm font-semibold text-brand-700 hover:underline"
        >
          {t("Înapoi la pagina principală", "Back to the home page")}
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-12 sm:px-6 sm:py-16">
      <h1 className="m-0 text-fluid-xl font-bold tracking-tight text-ink-900">
        {t("Retragerea din contract", "Withdrawing from the contract")}
      </h1>

      <p className="mt-4 text-fluid-sm leading-relaxed text-ink-600">
        {t(
          `Dacă sunteți consumator, aveți dreptul de a vă retrage din contractul încheiat cu ${COMPANY.legalName} în termen de 14 zile de la încheierea lui, fără a fi nevoie să vă justificați decizia — art. 9 din OUG nr. 34/2014. Formularul de mai jos este calea online pe care v-o punem la dispoziție potrivit art. 11¹ din aceeași ordonanță.`,
          `If you are a consumer, you have the right to withdraw from the contract concluded with ${COMPANY.legalName} within 14 days of concluding it, without giving reasons — art. 9 of Romanian Government Emergency Ordinance no. 34/2014. The form below is the online facility we provide under art. 11¹ of the same ordinance.`,
        )}
      </p>

      <p className="mt-3 text-fluid-sm leading-relaxed text-ink-600">
        {t(
          `O notificare neechivocă trimisă la ${COMPANY.email} este la fel de valabilă. Formularul este doar mai rapid și vă lasă o confirmare scrisă, cu data la care am înregistrat cererea.`,
          `An unequivocal notice sent to ${COMPANY.email} is equally valid. The form is simply faster and leaves you a written confirmation, stating the date we recorded the request.`,
        )}
      </p>

      <form onSubmit={submit} className="mt-8 flex flex-col gap-5" noValidate>
        <div>
          <label htmlFor="fullName" className={labelClass}>
            {t("Nume și prenume", "Full name")}
          </label>
          <input
            id="fullName"
            name="fullName"
            type="text"
            autoComplete="off"
            value={form.fullName}
            onChange={change}
            className={errors.fullName ? errorFieldClass : fieldClass}
            aria-invalid={errors.fullName ? "true" : undefined}
            aria-describedby={errors.fullName ? "fullName-error" : undefined}
          />
          {errors.fullName && (
            <span
              id="fullName-error"
              className="mt-1 block text-xs text-red-600"
            >
              {errors.fullName}
            </span>
          )}
        </div>

        <div>
          <label htmlFor="email" className={labelClass}>
            {t("E-mail", "E-mail")}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="off"
            value={form.email}
            onChange={change}
            className={errors.email ? errorFieldClass : fieldClass}
            aria-invalid={errors.email ? "true" : undefined}
            aria-describedby={errors.email ? "email-error" : undefined}
          />
          {errors.email && (
            <span id="email-error" className="mt-1 block text-xs text-red-600">
              {errors.email}
            </span>
          )}
        </div>

        <div>
          <label htmlFor="orderReference" className={labelClass}>
            {t("Număr comandă sau factură", "Order or invoice number")}{" "}
            <span className="font-normal text-ink-400">
              {t("(opțional)", "(optional)")}
            </span>
          </label>
          <input
            id="orderReference"
            name="orderReference"
            type="text"
            autoComplete="off"
            value={form.orderReference}
            onChange={change}
            className={fieldClass}
          />
          <span className="mt-1 block text-xs text-ink-400">
            {t(
              "Dacă îl aveți la îndemână. Cererea este valabilă și fără el.",
              "If you have it to hand. The request is valid without it.",
            )}
          </span>
        </div>

        <div>
          <label htmlFor="message" className={labelClass}>
            {t("Mesaj", "Message")}{" "}
            <span className="font-normal text-ink-400">
              {t("(opțional)", "(optional)")}
            </span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={form.message}
            onChange={change}
            className={fieldClass}
          />
          <span className="mt-1 block text-xs text-ink-400">
            {t(
              "Nu trebuie să vă justificați decizia — legea nu o cere și nici noi.",
              "You do not have to justify your decision — the law does not ask for it and neither do we.",
            )}
          </span>
        </div>

        {submitError && (
          <div
            className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-fluid-sm text-red-700"
            role="alert"
          >
            <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
            <span>{submitError}</span>
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={sending}
            className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-brand-600 bg-brand-600 px-5 py-2.5 text-fluid-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending
              ? t("Se trimite…", "Sending…")
              : t(
                  "Trimite cererea de retragere",
                  "Send the withdrawal request",
                )}
          </button>
        </div>
      </form>

      <p className="mt-8 border-t border-ink-100 pt-6 text-xs leading-relaxed text-ink-500">
        {t(
          "Atenție la o consecință a livrării imediate: dacă ați cerut începerea furnizării serviciului înainte de expirarea celor 14 zile și ați confirmat că înțelegeți acest lucru, dreptul de retragere se stinge în momentul în care serviciul a fost executat integral — art. 16 lit. a) din OUG nr. 34/2014.",
          "Note one consequence of immediate delivery: if you asked us to begin providing the service before the 14 days expired and confirmed that you understood this, the right of withdrawal lapses at the moment the service has been performed in full — art. 16(a) of OUG no. 34/2014.",
        )}
      </p>

      <p className="mt-3 text-xs text-ink-500">
        <Link to="/cancellation" className="text-brand-700 hover:underline">
          {t("Politica de anulare", "Cancellation Policy")}
        </Link>
        {" · "}
        <Link to="/terms" className="text-brand-700 hover:underline">
          {t("Termeni și condiții", "Terms and Conditions")}
        </Link>
        {" · "}
        <a
          href={REDRESS.anpcSal}
          target="_blank"
          rel="noreferrer"
          className="text-brand-700 hover:underline"
        >
          ANPC SAL
        </a>
      </p>
    </main>
  );
};

export default Withdrawal;
